import axios, { AxiosResponse } from "axios";
import {
  IRequestEnvelope,
  RequestFormValues,
} from "../models/Request";
import { IRequestNote, IRequestNoteEnvelope } from "../models/RequestNote";
import {
  IRequestReviewNote,
  IRequestReviewNoteEnvelope,
} from "../models/RequestReviewNote";
import {
  IRequestReviewDocumentNote,
  IRequestReviewDocumentNoteEnvelope,
} from "../models/RequestReviewDocumentNote";
import {
  CreateRequestReviewDocument,
  IRequestReviewDocumentEnvelope,
} from "../models/RequestReviewDocument";
import {
  IRequestReviewCreateForm,
  IRequestReviewEnvelope,
  IRequestReviewSubmitForm,
} from "../models/RequestReview";

import { IUser } from "../models/User";
import { toast } from "react-toastify";
import {msalConfig} from "../../msalConfig";
import authContext from "./msalAuth";
import {
  IRequestReviewValidationForm,
  IRequestReviewValidation,
} from "../models/RequestReviewValidation";
import { IRequestReviewUnlockForm } from "../models/RequestReviewUnlock";
import { IRequestReviewDocumentQuestionEnvelope } from "../models/RequestReviewDocumentQuestions";
import { IPriorReviewSummaryEnvelope } from "../models/PriorReviewSummary";
import { IRequestReviewDocumentAnswerFormValues } from "../models/RequestReviewDocumentAnswer";
import { ITestResults } from "../models/SelfTest";
import {
  IAdminSettingEnvelope,
  IAdminSettingFormValues,
} from "../models/AdminSetting";
import {
  AdminDocumentFormValues,
  IDocumentEnvelope,
} from "../models/AdminDocument";
import {
  AdminQuestionFormValues,
  IQuestionEnvelope,
} from "../models/AdminQuestion";
import {
  DocumentQuestionFormValues,
  IDocumentTypeDocumentQuestionEnvelope,
} from "../models/AdminDocumentQuestion";
import {
  DocumentQuestionRequirementFormValues,
} from "../models/AdminDocumentQuestionRequirement";
import {useAccount, useMsal} from "@azure/msal-react";
import {callMsGraph, getAccessToken} from "./MsGraphApiCall";
const { REACT_APP_API_URL } = process.env;

axios.defaults.baseURL = REACT_APP_API_URL;

axios.interceptors.request.use(async (config) => {
    let accessToken = await getAccessToken()
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(undefined, (error) => {
  console.log(error.message)
  //must be at top or else the const cant get populated and it fails
  if (error.message === "Network Error" && !error.response) {
    toast.error("Network Error- could not connect to server. You may be off of the VPN.");
  }
  else if (error.message.includes("interaction_required")){
    toast.error(
        error
    );
  }
  else {

    const { status, data, config } = error.response;

    let modalText = "";
    if (status === 404) {
      modalText = "404 Not Found.";
    }
    if (status === 401) {
      modalText = "401 Not Authorized.";
    }
    if (status === 418) {
      modalText = "Teapots Cannot.";
      // @ts-ignore
      this.props.history.push("./teapot");
      //history.push('./teapot')
    }
    if (status === 423) {
      modalText = "423 Locked.";
    }
    if (status === 409) {
      modalText = "409 Conflict.";
    }
    if (status === 403) {
      modalText = "403 Forbidden.";
    }
    if (
      status === 400 &&
      config.method === "get" &&
      data.errors.hasOwnProperty("id")
    ) {
      modalText = "400 Bad Request.";
    }

    if (status === 500) {
      toast.error("Something went wrong on the server side");
      console.log(data);
    }
    else {
      toast.error(
        modalText + " Error at " + data.errors.Name ??
          "" + " failed With the message: '" + data.errors.Message ??
          "" + ".' Please reload the page to get the latest data."
      );
      console.log(data);
    }
  }

  throw error.response;
});

const responsebody = (response: AxiosResponse) => response.data;

const sleep = (ms: number) => (responce: AxiosResponse) =>
  new Promise<AxiosResponse>((resolve) =>
    setTimeout(() => resolve(responce), ms)
  );

const api = {
  get: (url: string) => axios.get(url).then(sleep(1000)).then(responsebody),
  post: (url: string, body?: {}) =>
    axios.post(url, body).then(sleep(1000)).then(responsebody),
  put: (url: string, body: {}) =>
    axios.put(url, body).then(sleep(1000)).then(responsebody),
  del: (url: string) => axios.delete(url).then(sleep(1000)).then(responsebody),
};

const Requests = {
  list: (params: URLSearchParams): Promise<IRequestEnvelope> =>
    axios
      .get("/Request", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  details: (id: string) => api.get(`/Request/${id}`),
  update: (id: string, requestForm: RequestFormValues) =>
    api.put(`/Request/${id}`, requestForm),
  resend: (id: string) =>
    api.put(`/Request/resend/${id}`, ""),
};

const RequestNotes = {
  list: (requestId: string | undefined): Promise<IRequestNoteEnvelope> =>
    api.get(`/RequestNote/${requestId}`),
  details: (id: string) => api.get(`/RequestNote/${id}`),
  delete: (id: string) => api.del(`/RequestNote/${id}`),
  create: (requestNote: IRequestNote) => api.post("/RequestNote", requestNote),
  update: (requestNote: IRequestNote) =>
    api.put(`/RequestNote/${requestNote.id}`, requestNote),
};

const RequestReviewNotes = {
  list: (requestReviewId: string): Promise<IRequestReviewNoteEnvelope> =>
    api.get(`/RequestReviewNote/${requestReviewId}`),
  details: (id: string) => api.get(`/RequestReviewNote/${id}`),
  delete: (id: string) => api.del(`/RequestReviewNote/${id}`),
  create: (requestReviewNote: IRequestReviewNote) =>
    api.post("/RequestReviewNote", requestReviewNote),
  update: (requestReviewNote: IRequestReviewNote) =>
    api.put(`/RequestReviewNote/${requestReviewNote.id}`, requestReviewNote),
};

const RequestReviewDocumentNotes = {
  list: (
    params: URLSearchParams
  ): Promise<IRequestReviewDocumentNoteEnvelope> =>
    axios
      .get("/RequestReviewDocumentNote", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  details: (id: string) => api.get(`/RequestReviewDocumentNote/${id}`),
  delete: (id: string) => api.del(`/RequestReviewDocumentNote/${id}`),
  create: (requestReviewDocumentNote: IRequestReviewDocumentNote) =>
    api.post("/RequestReviewDocumentNote", requestReviewDocumentNote),
  update: (requestReviewDocumentNote: IRequestReviewDocumentNote) =>
    api.put(
      `/RequestReviewDocumentNote/${requestReviewDocumentNote.id}`,
      requestReviewDocumentNote
    ),
};

const RequestReviewDocuments = {
  list: (requestReviewId: string): Promise<IRequestReviewDocumentEnvelope> =>
    api.get(`/RequestReviewDocument/${requestReviewId}`),

  details: (id: string) => api.get(`/RequestReviewDocument/${id}`),
  create: (requestReviewDocument: CreateRequestReviewDocument) =>
    api.post("/RequestReviewDocument", requestReviewDocument),
  delete: (id: string) => api.del(`/RequestReviewDocument/${id}`),
};

const RequestReviews = {
  list: (params: URLSearchParams): Promise<IRequestReviewEnvelope> =>
    axios
      .get("/RequestReview", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  details: (id: string) => api.get(`/RequestReview/${id}`),
  create: (requestId: IRequestReviewCreateForm) =>
    api.post(`/RequestReview/`, requestId),
  update: (requestReviewForm: IRequestReviewSubmitForm) =>
    api.put(`/RequestReview/`, requestReviewForm),
};

const RequestReviewDocumentAnswer = {
  Answer: (
    RequestReviewDocumentId: string,
    RequestReviewDocumentAnswerFormValue: IRequestReviewDocumentAnswerFormValues
  ) =>
    api.post(
      `/RequestReviewDocumentAnswer/${RequestReviewDocumentId}`,
      RequestReviewDocumentAnswerFormValue
    ),

  Answers: (
    RequestReviewDocumentAnswerFormValues: IRequestReviewDocumentAnswerFormValues[]
  ) =>
    api.post(
      `/RequestReviewDocumentAnswer/`,
      RequestReviewDocumentAnswerFormValues
    ),
};

const RequestReviewValidation = {
  validate: (
    RequestValidation: IRequestReviewValidationForm
  ): Promise<IRequestReviewValidation> =>
    api.post(`/RequestReviewValidation`, RequestValidation),
};

const RequestReviewUnlock = {
  unlock: (id: IRequestReviewUnlockForm) => api.put(`/RequestReviewUnlock`, id),
};

const RequestReviewDocumentQuestions = {
  list: (): Promise<IRequestReviewDocumentQuestionEnvelope> =>
    api.get(`/RequestReviewDocumentQuestion/`),

  hash: (): Promise<string> => api.put(`/RequestReviewDocumentQuestion/`, ""),
};

const SelfTest = {
  test: (): Promise<ITestResults> => api.get(`/SelfTest`),
}

const PriorReviewSummary = {
  list: (requestId: string | undefined): Promise<IPriorReviewSummaryEnvelope> =>
    api.get(`/PriorReviewSummary/${requestId}`),
};

const RequestPDF = {
  details: (id: string | null) => api.get(`/RequestPDF/${id}`),
};

const User = {
  current: (): Promise<IUser> => api.get("/User"),
};

const AdminSetting = {
  list: (params: URLSearchParams): Promise<IAdminSettingEnvelope> =>
    axios
      .get("/AdminSetting", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  update: (adminSetting: IAdminSettingFormValues) =>
    api.put(`/AdminSetting/`, adminSetting),
};

const AdminDocument = {
  list: (params: URLSearchParams): Promise<IDocumentEnvelope> =>
    axios
      .get("/AdminDocument", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  create: (document: AdminDocumentFormValues) =>
    api.post(`/AdminDocument/`, document),
  update: (document: AdminDocumentFormValues) =>
    api.put(`/AdminDocument/`, document),
};

const AdminQuestion = {
  list: (params: URLSearchParams): Promise<IQuestionEnvelope> =>
    axios
      .get("/AdminQuestion", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  create: (question: AdminQuestionFormValues) =>
    api.post(`/AdminQuestion/`, question),
  update: (question: AdminQuestionFormValues) =>
    api.put(`/AdminQuestion/`, question),
};

const AdminDocumentQuestion = {
  list: (
    params: URLSearchParams
  ): Promise<IDocumentTypeDocumentQuestionEnvelope> =>
    axios
      .get("/AdminDocumentQuestion", { params: params })
      .then(sleep(1000))
      .then(responsebody),

  create: (doumentQuestion: DocumentQuestionFormValues) =>
    api.post(`/AdminDocumentQuestion/`, doumentQuestion),
  update: (documentQuestion: DocumentQuestionFormValues) =>
    api.put(`/AdminDocumentQuestion/`, documentQuestion),
  delete: (documentId: string, questionId: string) =>
    api.del(`/AdminDocumentQuestion/${documentId}/${questionId}`),
};

const AdminDocumentQuestionRequirement = {
  update: (
    documentQuestionRequirement: DocumentQuestionRequirementFormValues
  ) =>
    api.put(`/AdminDocumentQuestionRequirement/`, documentQuestionRequirement),
};

export default {
  PriorReviewSummary,

  Requests,
  RequestReviews,
  RequestReviewDocuments,

  RequestReviewDocumentAnswer,
  RequestReviewDocumentQuestions,
  RequestReviewValidation,
  RequestReviewUnlock,
  RequestPDF,

  RequestNotes,
  RequestReviewNotes,
  RequestReviewDocumentNotes,

  SelfTest,

  User,

  AdminSetting,
  AdminQuestion,
  AdminDocumentQuestionRequirement,
  AdminDocumentQuestion,
  AdminDocument,
};
